import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { MULTIPLE_COMPARISONS_CORRECTION_CALCULATOR_STRINGS } from "@/components/tools/multiple-comparisons-correction-calculator";
import { RankPlot } from "@/components/tools/stats-descriptive-more/rank-plot";
import { t } from "@/lib/i18n/interpolate";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  machineNumber,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { toCsv } from "@/lib/tool-kit/stats-descriptive/frequency";
import {
  ADJUST_METHODS,
  type AdjustMethod,
  type AdjustReport,
  errorRate,
} from "@/lib/tool-kit/stats-descriptive-more/multiple-comparisons";

type Strings = typeof MULTIPLE_COMPARISONS_CORRECTION_CALCULATOR_STRINGS;

export interface ComparisonView {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
}

export function methodName(method: AdjustMethod, strings: Strings): string {
  const names: Record<AdjustMethod, string> = {
    bonferroni: strings.nameBonferroni,
    sidak: strings.nameSidak,
    holm: strings.nameHolm,
    holmSidak: strings.nameHolmSidak,
    hochberg: strings.nameHochberg,
    hommel: strings.nameHommel,
    bh: strings.nameBh,
    by: strings.nameBy,
  };
  return names[method];
}

function methodRule(method: AdjustMethod, strings: Strings): string {
  const rules: Record<AdjustMethod, string> = {
    bonferroni: strings.ruleBonferroni,
    sidak: strings.ruleSidak,
    holm: strings.ruleHolm,
    holmSidak: strings.ruleHolmSidak,
    hochberg: strings.ruleHochberg,
    hommel: strings.ruleHommel,
    bh: strings.ruleBh,
    by: strings.ruleBy,
  };
  return rules[method];
}

/** Raw and adjusted p-values for every test; a star marks adjusted p at or below alpha. */
export function adjustedRows(
  report: AdjustReport,
  view: ComparisonView
): string[][] {
  const { strings, num } = view;
  const mark = (value: number) =>
    value <= report.alpha ? `${num(value)}${strings.sigMark}` : num(value);
  return report.rows.map((row, i) => [
    row.label || t(strings.testLabel, { n: view.fmt(i + 1) }),
    mark(row.p),
    ...ADJUST_METHODS.map((method) => mark(row.adjusted[method])),
  ]);
}

export function summaryRows(
  report: AdjustReport,
  view: ComparisonView
): string[][] {
  const { strings, fmt } = view;
  return ADJUST_METHODS.map((method) => [
    methodName(method, strings),
    errorRate(method) === "fwer" ? strings.fwer : strings.fdr,
    methodRule(method, strings),
    fmt(report.rejected[method]),
  ]);
}

/** Holm and BH step by step on the sorted p-values. */
export function stepRows(
  report: AdjustReport,
  view: ComparisonView
): string[][] {
  const { fmt, num } = view;
  const m = report.m;
  const order = report.rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => a.row.p - b.row.p || a.index - b.index);
  return order.map(({ row }, i) => {
    const rank = i + 1;
    return [
      fmt(rank),
      num(row.p),
      fmt(m - rank + 1),
      num((m - rank + 1) * row.p),
      num(row.adjusted.holm),
      num((m / rank) * row.p),
      num(row.adjusted.bh),
    ];
  });
}

export function ThresholdPlot({
  report,
  view,
}: {
  report: AdjustReport;
  view: ComparisonView;
}) {
  const { strings, fmt } = view;
  const { alpha, m } = report;
  const sorted = report.rows.map((row) => row.p).sort((a, b) => a - b);
  const ranks = sorted.map((_, i) => i + 1);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
      <RankPlot
        fmt={fmt}
        label={t(strings.plotLabel, {
          n: fmt(sorted.length),
          alpha: view.num(alpha),
        })}
        lines={[
          {
            label: strings.lineBonferroni,
            values: ranks.map(() => alpha / m),
            color: CHART_COLORS[3],
            dash: "2 4",
          },
          {
            label: strings.lineHolm,
            values: ranks.map((rank) => alpha / (m - rank + 1)),
            color: CHART_COLORS[1],
            dash: "6 4",
          },
          {
            label: strings.lineBh,
            values: ranks.map((rank) => (alpha * rank) / m),
            color: CHART_COLORS[2],
          },
        ]}
        pLabel={strings.plotP}
        pointsLabel={strings.plotPoints}
        rankLabel={strings.plotRank}
        sorted={sorted}
      />
      <p className="text-muted-foreground text-xs">{strings.plotNote}</p>
    </div>
  );
}

/** The adjusted p-values as CSV with ASCII numbers, whatever the page's locale. */
export function csvText(report: AdjustReport, view: ComparisonView): string {
  const { strings } = view;
  return toCsv(
    [
      strings.colTest,
      strings.colRaw,
      ...ADJUST_METHODS.map((method) => methodName(method, strings)),
    ],
    report.rows.map((row, i) => [
      row.label || t(strings.testLabel, { n: String(i + 1) }),
      machineNumber(row.p, 8),
      ...ADJUST_METHODS.map((method) => machineNumber(row.adjusted[method], 8)),
    ])
  );
}
